import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Upload, File as FileIcon, Loader2 } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import api from '@/services/api';

export type DocumentType = 'ID_PROOF' | 'EDUCATION_CERT' | 'WORK_PROOF' | 'RECOMMENDATION_LETTER';

export interface HelperDocumentItem {
  id: number;
  documentType: DocumentType;
  fileName: string;
  fileUrl: string;
  file?: File;
  mimeType?: string;
  fileSizeBytes?: number;
  verified: boolean;
  uploadedAt: string;
}

interface FileUploadListProps {
  helperId: number;
  documentType: DocumentType;
  workExperienceId?: number;
  documents: HelperDocumentItem[];
  onChange: (docs: HelperDocumentItem[]) => void;
  accept?: string;
  maxFileSizeMB?: number;
  disabled?: boolean;
}

export function FileUploadList({
  helperId,
  documentType,
  workExperienceId,
  documents,
  onChange,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxFileSizeMB = 10,
  disabled = false,
}: FileUploadListProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMsg(null);

    // Validate size
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setErrorMsg(t('helperProfileForm.documents.fileTooLarge', { size: maxFileSizeMB }) || `File too large (max ${maxFileSizeMB}MB)`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
    
    const isAccepted = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type;
      }
      return file.type.match(new RegExp(type.replace('*', '.*')));
    });

    if (!isAccepted && !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      setErrorMsg(t('helperProfileForm.documents.unsupportedType') || 'Unsupported file type');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Defer upload for work-experience-scoped docs until the experience exists.
    // Without a workExperienceId these would be saved as orphan profile-level docs.
    // Also defer when the profile itself doesn't exist yet (helperId=0 → 儲存後由表單統一上傳)。
    const requiresWorkExperience = documentType === 'RECOMMENDATION_LETTER' || documentType === 'WORK_PROOF';
    if ((requiresWorkExperience && workExperienceId === undefined) || !helperId) {
      onChange([
        ...documents,
        {
          id: 0,
          documentType,
          fileName: file.name,
          fileUrl: '',
          file,
          mimeType: file.type,
          fileSizeBytes: file.size,
          verified: false,
          uploadedAt: new Date().toISOString(),
        },
      ]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);
      if (workExperienceId !== undefined) {
        formData.append('workExperienceId', workExperienceId.toString());
      }

      // Backend returns DocumentResponse directly (NOT wrapped in ApiResponse).
      // After axios response interceptor unwraps `response.data`, we get the document object itself.
      const uploadedDoc = await api.post(`/helpers/${helperId}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }) as unknown as HelperDocumentItem;

      if (!uploadedDoc || typeof uploadedDoc.id !== 'number') {
        throw new Error('Invalid upload response');
      }

      onChange([...documents, uploadedDoc]);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.message || err?.message || 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async (doc: HelperDocumentItem) => {
    setErrorMsg(null);
    // Pending (not-yet-uploaded) docs: drop from local list, nothing to delete remotely.
    if (!doc.id) {
      onChange(documents.filter(d => d !== doc));
      return;
    }
    try {
      await api.delete(`/helpers/${helperId}/documents/${doc.id}`);
      onChange(documents.filter(d => d.id !== doc.id));
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.message || err?.message || 'Remove failed');
    }
  };

  return (
    <div className={cn("space-y-4", disabled && "opacity-60 pointer-events-none")}>
      {/* Document List */}
      {documents.length > 0 && (
        <ul className="space-y-2">
          {documents.filter((doc) => doc && (doc.id !== undefined || doc.file)).map((doc, idx) => (
            <li
              key={doc.id || `pending-${idx}-${doc.fileName}`}
              className="flex items-center justify-between p-3 border rounded-md bg-background"
            >
              <div className="flex items-center space-x-3 overflow-hidden">
                <FileIcon className="w-5 h-5 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate" title={doc.fileName}>
                  {doc.fileName}
                  {!doc.id && <span className="ml-2 text-xs text-amber-600">(pending)</span>}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => handleRemove(doc)}
                disabled={disabled || isUploading}
                type="button"
                aria-label={t('helperProfileForm.documents.removeButton') || 'Remove'}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Upload Error */}
      {errorMsg && (
        <div className="text-sm text-destructive font-medium">{errorMsg}</div>
      )}

      {/* Upload Button */}
      <div>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled || isUploading}
        />
        <Button
          type="button"
          variant="outline"
          className="w-full border-dashed"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {isUploading ? t('helperProfileForm.uploading') || 'Uploading...' : t('helperProfileForm.documents.uploadButton') || 'Upload Document'}
        </Button>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t('helperProfileForm.documents.maxSize', { n: maxFileSizeMB, defaultValue: `PDF / JPG / PNG · max {{n}} MB` })}
        </p>
      </div>
    </div>
  );
}
